/**
 * Read-only visualization of a conformity obligation's `applicability`
 * (Geltungsbereich) and `predicate` (Prädikat) as labelled expression trees:
 *
 *   mode="rule"      — the rule as authored (SPEC-conformity-algebra Expr).
 *   mode="evaluated" — coloured by the engine's `ConformityResult` (T/F/U), with
 *                      decisive leaves highlighted and U leaves pointing at the
 *                      missing datum.
 *
 * Grammar + result types are central in @the-real-insight/tri-model; the portal
 * never evaluates. The spec trace is FLAT (`Evaluated.leaves` + `decisive`): atom
 * tones come from the matched leaves, bool-node tones are folded (strong Kleene)
 * from their children, and a part Quant carries its body's verdict.
 */
import "../administration/din276/din276Tree.css";
import "./conformityTree.css";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Scale, MapPin, CheckCircle2, XCircle, HelpCircle, MinusCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { ReviewStatusBadge } from "../administration/operator-responsibility/legalDisplay";
import { expandUnit, matchNorm, isHarmonized } from "../administration/din276/din276Tree";
import {
  CMP_LABEL,
  CMP_TOOLTIP,
  SOURCE_LABEL,
  normKindClass,
  tvClass,
  outcomeClass,
} from "./conformityDisplay";
import { isAtom, isQuant, isCount } from "./conformityTypes";
import type {
  AtomExpr,
  Expr,
  QuantExpr,
  CountExpr,
  Tri,
  Leaf,
  Evaluated,
  Conformity,
  ConformityResult,
  ConformityStatus,
  ConformityObligation,
  UnitResolver,
} from "./conformityTypes";

/** Fluent-TooltipHost-Ersatz: gleiches Aufrufmuster (content + Kinder). */
function TooltipHost({ content, children }: { content: any; children: any }) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{children}</span>
        </TooltipTrigger>
        <TooltipContent>{content}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** The translation function as returned by react-i18next's useTranslation. */
type TFunc = ReturnType<typeof useTranslation>["t"];

interface Props {
  obligation: ConformityObligation;
  mode: "rule" | "evaluated";
  /** Required for mode="evaluated": the engine's ConformityResult. */
  result?: ConformityResult;
  /** Resolve a canonical attribute's unit (typeCode of the enclosing Quant, attr). */
  unitOf?: UnitResolver;
  /** Mode A: source provision text shown beside the formalization. */
  provisionText?: string;
  provisionCitation?: string;
}

const OP_LABEL: Record<string, string> = { and: "UND", or: "ODER", not: "NICHT" };
const QUANT_LABEL: Record<string, string> = { exists: "ES GIBT", forall: "FÜR ALLE", count: "ANZAHL" };

const kAnd = (vs: Tri[]): Tri => (vs.includes("F") ? "F" : vs.includes("U") ? "U" : "T");
const kOr = (vs: Tri[]): Tri => (vs.includes("T") ? "T" : vs.includes("U") ? "U" : "F");
const kNot = (v: Tri): Tri => (v === "T" ? "F" : v === "F" ? "T" : "U");

function valueText(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map((x) => String(x)).join(", ")}]`;
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "ja" : "nein";
  return String(v);
}

const atomSig = (source: string, attr: string, cmp: string) => `${source}|${attr}|${cmp}`;
const leafSig = (l: Leaf) =>
  `${l.kind}|${l.source ?? ""}|${l.attr ?? ""}|${l.cmp ?? ""}|${l.typeCode ?? ""}|${l.value}`;

/** A per-stage reader over the spec's flat `Evaluated.leaves` + the `decisive` set. */
function makeStage(ev: Evaluated | undefined, decisive: Leaf[]) {
  const bySig = new Map<string, { leaves: Leaf[]; i: number }>();
  const quantLeaves: Leaf[] = [];
  for (const l of ev?.leaves ?? []) {
    if (l.kind === "atom" && l.source && l.attr && l.cmp) {
      const k = atomSig(l.source, l.attr, l.cmp);
      if (!bySig.has(k)) bySig.set(k, { leaves: [], i: 0 });
      bySig.get(k)!.leaves.push(l);
    } else if (l.typeCode) {
      quantLeaves.push(l); // empty-set quant leaf / count leaf
    }
  }
  const decSet = new Set(decisive.map(leafSig));
  return {
    /** next atom leaf for this signature (declaration order); undefined in rule mode. */
    atom(source: string, attr: string, cmp: string): Leaf | undefined {
      const e = bySig.get(atomSig(source, attr, cmp));
      if (!e || e.i >= e.leaves.length) return undefined;
      return e.leaves[e.i++];
    },
    /** a part Quant: its empty-set/count leaf, else the remaining body-atom leaves. */
    quant(typeCode: string, body: AtomExpr | null): { leaves: Leaf[]; standalone?: Leaf } {
      const standalone = quantLeaves.find((l) => l.typeCode === typeCode);
      if (standalone) return { leaves: [standalone], standalone };
      if (!body) return { leaves: [] };
      const e = bySig.get(atomSig("asset", body.attr, body.cmp));
      const leaves = e ? e.leaves.slice(e.i) : [];
      if (e) e.i = e.leaves.length;
      return { leaves };
    },
    isDecisive: (l: Leaf | undefined) => !!l && decSet.has(leafSig(l)),
  };
}
type Stage = ReturnType<typeof makeStage>;

export default function ConformityPredicateView({
  obligation,
  mode,
  result,
  unitOf,
  provisionText,
  provisionCitation,
}: Props) {
  const { t } = useTranslation();
  const conformity: Partial<Conformity> = obligation.conformity || {};
  const advisory =
    mode === "evaluated" ? !!result?.advisoryOnly : obligation.reviewStatus !== "confirmed";
  const norm = matchNorm(obligation.source);

  const nodeClass = (tv: Tri | undefined, decisive: boolean, demanded: boolean): string => {
    let c = "conf-node";
    if (tv) {
      c += " " + tvClass(tv);
      if (decisive) c += " decisive";
      if (tv === "U" && !demanded) c += " not-demanded";
    }
    return c;
  };

  const missingChip = (leaf: Leaf | undefined) =>
    leaf?.missing ? (
      <span className="conf-missing">
        <MapPin size={11} />
        <span className="conf-where">{SOURCE_LABEL[leaf.missing.where] || leaf.missing.where}</span>
        <span className="conf-mute">
          · {leaf.missing.reasonKind === "scope" ? t("conformity.scope", "Geltungsbereich") : t("conformity.predicate", "Prädikat")}
        </span>
      </span>
    ) : null;

  const atomRow = (atom: AtomExpr, leaf: Leaf | undefined, decisive: boolean, typeCode: string | undefined, indent: number, demanded: boolean) => {
    const unit = unitOf?.(typeCode, atom.attr) ?? null;
    const tv = leaf?.value;
    return (
      <div className={nodeClass(tv, decisive, demanded)} style={{ paddingLeft: indent }}>
        {tv && <span className="conf-tv">{tv}</span>}
        <span className="conf-atom">
          <span className="conf-src">{SOURCE_LABEL[atom.source] || atom.source}</span>
          {typeCode && <span className="din276tree-code">{typeCode}</span>}
          <span className="conf-field">{atom.attr}</span>
          <TooltipHost content={<span className="text-xs">{CMP_TOOLTIP[atom.cmp] || atom.cmp}</span>}>
            <span className="conf-cmp">{CMP_LABEL[atom.cmp] || atom.cmp}</span>
          </TooltipHost>
          {atom.cmp !== "exists" && <span className="conf-value">{valueText(atom.value)}</span>}
          {unit && (
            <TooltipHost content={<span className="text-xs">{unit} — {expandUnit(unit)}</span>}>
              <span className="conf-unit">{unit}</span>
            </TooltipHost>
          )}
        </span>
        {missingChip(leaf)}
      </div>
    );
  };

  /** Render an Expr → [node, tv]. `stage` undefined = rule mode (no tones). */
  const renderExpr = (expr: Expr, stage: Stage | undefined, depth: number, demanded: boolean, typeCode?: string): [any, Tri | undefined] => {
    const indent = 8 + depth * 18;

    if (isAtom(expr)) {
      const leaf = stage?.atom(expr.source, expr.attr, expr.cmp);
      return [atomRow(expr, leaf, !!stage?.isDecisive(leaf), typeCode, indent, demanded), leaf?.value];
    }

    if (isQuant(expr) || isCount(expr)) {
      const q = expr as QuantExpr | CountExpr;
      const body = isAtom(q.body) ? (q.body as AtomExpr) : null;
      const qr = stage?.quant(q.typeCode, body);
      let tv: Tri | undefined;
      let decisive = false;
      if (qr) {
        if (qr.standalone) {
          tv = qr.standalone.value;
          decisive = stage!.isDecisive(qr.standalone);
        } else {
          const vs = qr.leaves.map((l) => l.value);
          tv = q.kind === "forall" ? kAnd(vs) : kOr(vs);
          decisive = qr.leaves.some((l) => stage!.isDecisive(l));
        }
      }
      const head = (
        <div className={nodeClass(tv, decisive, demanded)} style={{ paddingLeft: indent }}>
          {tv && <span className="conf-tv">{tv}</span>}
          <span className="conf-op op-quant">{QUANT_LABEL[q.kind] || q.kind}</span>
          <span className="din276tree-code">{q.typeCode}</span>
          {q.partRole && (
            <TooltipHost content={<span className="text-xs">{t("conformity.partRole", "Teil-Rolle")}: {q.partRole}</span>}>
              <span className="conf-partrole">{q.partRole}</span>
            </TooltipHost>
          )}
          {isCount(q) && <span className="conf-cmp">{CMP_LABEL[(q as CountExpr).cmp] || (q as CountExpr).cmp} {(q as CountExpr).n}</span>}
          {qr?.standalone && missingChip(qr.standalone)}
        </div>
      );
      // body shown as the rule (no tone, no re-consume): atoms read source:"asset"; carry the Quant typeCode for units.
      const [bodyNode] = renderExpr(q.body, undefined, depth + 1, demanded, q.typeCode);
      return [<Fragment>{head}{bodyNode}</Fragment>, tv];
    }

    // const — a literal truth value (compiler-emitted trivial branch)
    if (expr.kind === "const") {
      const tv = stage ? expr.value : undefined;
      const constLabel = expr.value === "T" ? "WAHR" : expr.value === "F" ? "FALSCH" : "U";
      return [
        <div className={nodeClass(tv, false, demanded)} style={{ paddingLeft: indent }}>
          {tv && <span className="conf-tv">{tv}</span>}
          <span className="conf-op op-const">{constLabel}</span>
        </div>,
        tv,
      ];
    }

    // bool (and / or / not)
    const args = expr.kind === "not" ? [expr.arg] : expr.args;
    const childNodes: any[] = [];
    const childTvs: Tri[] = [];
    args.forEach((child) => {
      const demandedChild = childDemanded(expr.kind, childTvs, demanded);
      const [node, tv] = renderExpr(child, stage, depth + 1, demandedChild, typeCode);
      childNodes.push(node);
      if (tv) childTvs.push(tv);
    });
    const tv: Tri | undefined =
      stage && childTvs.length === args.length
        ? expr.kind === "and" ? kAnd(childTvs) : expr.kind === "or" ? kOr(childTvs) : kNot(childTvs[0])
        : undefined;
    const head = (
      <div className={nodeClass(tv, false, demanded)} style={{ paddingLeft: indent }}>
        {tv && <span className="conf-tv">{tv}</span>}
        <span className={`conf-op op-${expr.kind}`}>{OP_LABEL[expr.kind] || expr.kind}</span>
        {expr.kind !== "not" && (
          <span className="conf-mute text-xs">{args.length} {t("conformity.operands", "Bedingungen")}</span>
        )}
      </div>
    );
    return [<Fragment>{head}{childNodes.map((n, i) => <Fragment key={i}>{n}</Fragment>)}</Fragment>, tv];
  };

  const renderStage = (key: "applicability" | "predicate", expr: Expr | undefined) => {
    if (!expr) return null;
    const label = key === "applicability"
      ? t("conformity.applicability", "Geltungsbereich")
      : t("conformity.predicate", "Prädikat");
    const ev = mode === "evaluated" ? (key === "applicability" ? result?.applicabilityTrace : result?.predicateTrace) : undefined;
    const stage = mode === "evaluated" && ev ? makeStage(ev, result?.decisive ?? []) : undefined;
    const [tree] = renderExpr(expr, stage, 0, true);
    return (
      <div className="conf-stage">
        <div className="conf-stage-label">
          <span className="conf-stage-kicker">{label}</span>
          {key === "applicability" && (
            <span className="conf-mute">— {t("conformity.applicabilityHint", "wann gilt die Regel?")}</span>
          )}
        </div>
        {mode === "evaluated" && key === "applicability" && ev && <ScopeBanner tv={ev.value} t={t} />}
        {mode === "evaluated" && key === "predicate" && ev && result && <OutcomeBanner outcome={result.status} t={t} />}
        <div className="conf-tree din276tree">{tree}</div>
      </div>
    );
  };

  const header = (
    <div className="conf-head">
      <ReviewStatusBadge status={obligation.reviewStatus} />
      {advisory && (
        <TooltipHost content={<span className="text-xs">{t("conformity.advisoryHint", "Prädikat ungeprüft — Verdikt nur beratend, nie bindend.")}</span>}>
          <span className="conf-advisory">{t("conformity.advisoryOnly", "advisory")}</span>
        </TooltipHost>
      )}
      {obligation.source && (
        <TooltipHost
          content={
            <div className="text-xs">
              <div><b>{obligation.source}</b>{norm ? ` — ${norm.title}` : ""}</div>
              {norm && <div>{t("conformity.governs", "regelt")}: {norm.governs}</div>}
              {isHarmonized(obligation.source) && <div>{t("conformity.harmonized", "harmonisiert (hEN/hEPN)")}</div>}
            </div>
          }
        >
          <span className={`din276tree-norm norm-${normKindClass(obligation.source)}`}>
            <Scale size={11} />
            {obligation.source}
            {isHarmonized(obligation.source) && <span className="din276tree-hen">hEN</span>}
          </span>
        </TooltipHost>
      )}
      {obligation.modality && (
        <span className="conf-mute text-xs">{t(`conformity.modality.${obligation.modality}`, obligation.modality)}</span>
      )}
    </div>
  );

  const stages = (
    <>
      {renderStage("applicability", conformity.applicability)}
      {renderStage("predicate", conformity.predicate)}
      {!conformity.applicability && !conformity.predicate && (
        <div className="conf-mute textM marginTopS">{t("conformity.empty", "Kein formalisiertes Prädikat.")}</div>
      )}
    </>
  );

  if (mode === "evaluated") {
    return (
      <div className="conf">
        {result && <OutcomeBanner outcome={result.status} t={t} headline advisory={advisory} />}
        {stages}
      </div>
    );
  }

  return (
    <div className="conf">
      {header}
      {provisionText ? (
        <div className="conf-review">
          <div className="conf-provision">
            {provisionCitation && <div className="conf-provision-cite">{provisionCitation}</div>}
            {provisionText}
          </div>
          <div className="conf-formalization">{stages}</div>
        </div>
      ) : (
        stages
      )}
    </div>
  );
}

/** A child is demanded unless the parent already short-circuited (satisfied OR / falsified AND). */
function childDemanded(op: string, siblingTvs: Tri[], parentDemanded: boolean): boolean {
  if (!parentDemanded) return false;
  if (op === "or" && siblingTvs.includes("T")) return false;
  if (op === "and" && siblingTvs.includes("F")) return false;
  return true;
}

function OutcomeBanner({
  outcome,
  t,
  headline,
  advisory,
}: {
  outcome: ConformityStatus;
  t: TFunc;
  headline?: boolean;
  advisory?: boolean;
}) {
  const Icon =
    outcome === "Compliant" ? CheckCircle2 : outcome === "Violated" ? XCircle : outcome === "NotDeterminable" ? HelpCircle : MinusCircle;
  const label = t(`conformity.outcome.${outcome}`, outcome);
  const sub =
    outcome === "NotDeterminable"
      ? t("conformity.outcome.undeterminedHint", "Datum fehlt — siehe markierte Knoten.")
      : outcome === "NotApplicable"
        ? t("conformity.outcome.notApplicableHint", "Regel gilt für dieses Asset nicht.")
        : "";
  return (
    <div className={`conf-banner ${outcomeClass(outcome)}`}>
      <Icon size={16} />
      <span>{label}</span>
      {sub && <span className="conf-banner-sub">{sub}</span>}
      {headline && advisory && (
        <span className="conf-advisory" style={{ marginLeft: "auto" }}>
          {t("conformity.advisoryOnly", "advisory")}
        </span>
      )}
    </div>
  );
}

/** Applicability-stage banner — scope semantics from the stage root value. */
function ScopeBanner({ tv, t }: { tv: Tri | undefined; t: TFunc }) {
  if (tv === "T") {
    return (
      <div className="conf-banner out-compliant">
        <CheckCircle2 size={16} />
        <span>{t("conformity.scopeApplies", "Regel gilt")}</span>
      </div>
    );
  }
  if (tv === "F") {
    return (
      <div className="conf-banner out-na">
        <MinusCircle size={16} />
        <span>{t("conformity.scopeNotApplicable", "Regel gilt nicht")}</span>
      </div>
    );
  }
  return (
    <div className="conf-banner out-undetermined">
      <HelpCircle size={16} />
      <span>{t("conformity.scopeUndetermined", "Geltungsbereich unbestimmt")}</span>
      <span className="conf-banner-sub">{t("conformity.scopeUndeterminedHint", "Meist fehlender Gebäudebezug.")}</span>
    </div>
  );
}
